import React, { useMemo } from 'react';
import { Clock, LogIn, User, Ticket, Moon } from 'lucide-react';
import { formatCountdownTime } from './guestReservationFormFormat';
import { GuestReservationFormExtrasAndPayment } from './GuestReservationFormExtrasAndPayment';
import { GuestReservationFormPersonalAndStayFields } from './GuestReservationFormPersonalAndStayFields';
import {
  GuestReservationStepTracker,
  type GuestReservationWizardStep,
} from './GuestReservationStepTracker';
import { useGuestReservationForm } from './useGuestReservationForm';
import { SheetBackButton } from './SheetBackButton';

export function GuestReservationFormView({ m }: { m: ReturnType<typeof useGuestReservationForm> }) {
  const {
    t,
    onClose,
    itemId,
    itemName,
    itemType,
    itemPrice,
    currentUser,
    paymentMode,
    setPaymentMode,
    isLoggedIn,
    setIsLoggedIn,
    loginData,
    handleLoginInputChange,
    loginError,
    timeRemaining,
    handleLogin,
    handleSubmit,
    isTimeLow,
    countdownStarted,
    vumPromo,
    formData,
    wizardStep,
    setWizardStep,
    paymentError,
  } = m;

  const formReady = paymentMode === 'guest' || (paymentMode === 'member' && isLoggedIn);

  const step1Done = useMemo(() => {
    if (itemType === 'activity') return Boolean(formData.checkIn) && formData.adults >= 1;
    return Boolean(formData.checkIn && formData.checkOut) && formData.adults >= 1;
  }, [itemType, formData.checkIn, formData.checkOut, formData.adults]);

  const step2Done = useMemo(() => {
    if (currentUser) return Boolean(formData.postalCode && formData.province && formData.country);
    return Boolean(
      formData.firstName &&
        formData.lastName &&
        formData.email &&
        formData.phone &&
        formData.postalCode &&
        formData.province &&
        formData.country,
    );
  }, [currentUser, formData]);

  const step3Done = Boolean(formData.hearAboutUs);

  const goNext = () => {
    if (wizardStep === 1 && !step1Done) return;
    if (wizardStep === 2 && !step2Done) return;
    setWizardStep((s) => Math.min(3, s + 1) as GuestReservationWizardStep);
  };

  const goBack = () => {
    if (wizardStep === 1) {
      onClose();
      return;
    }
    setWizardStep((s) => Math.max(1, s - 1) as GuestReservationWizardStep);
  };

  const selectStep = (step: GuestReservationWizardStep) => {
    if (step === 1) {
      setWizardStep(1);
      return;
    }
    if (step === 2 && step1Done) {
      setWizardStep(2);
      return;
    }
    if (step === 3 && step1Done && step2Done) {
      setWizardStep(3);
    }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-[#ece3de]">
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-black/10 p-3">
        <SheetBackButton phone="inline" onClick={goBack} />
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h2
            key={`title-${itemId}`}
            className="truncate text-base font-bold text-[#3d2817] md:text-lg"
          >
            {t('guestReservation.quickReservation')}
          </h2>
          {countdownStarted ? (
            <div
              className={`flex shrink-0 items-center space-x-2 rounded-full px-2.5 py-1 ${
                isTimeLow ? 'bg-red-100 text-red-700' : 'bg-[#2f9e44]/15 text-[#2f9e44]'
              }`}
            >
              <Clock className={`h-3.5 w-3.5 ${isTimeLow ? 'animate-pulse' : ''}`} />
              <span className="font-mono text-xs font-semibold">
                {formatCountdownTime(timeRemaining)}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] pb-4">
        {!currentUser && (
          <div className="border-b border-black/10 p-4">
            <label className="mb-3 block text-sm font-medium text-[#3d2817]">
              {t('guestReservation.paymentMode')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setPaymentMode('guest');
                  setIsLoggedIn(false);
                }}
                className={`rounded-xl border-2 p-3 transition-all ${
                  paymentMode === 'guest'
                    ? 'border-[#2f9e44] bg-[#2f9e44]/10 text-[#2f9e44]'
                    : 'border-[#3d2817]/15 bg-white/80 text-[#3d2817] hover:border-[#3d2817]/30'
                }`}
              >
                <User className="mx-auto mb-1.5 h-5 w-5" />
                <div className="text-sm font-semibold">{t('guestReservation.payAsGuest')}</div>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMode('member')}
                className={`rounded-xl border-2 p-3 transition-all ${
                  paymentMode === 'member'
                    ? 'border-[#2f9e44] bg-[#2f9e44]/10 text-[#2f9e44]'
                    : 'border-[#3d2817]/15 bg-white/80 text-[#3d2817] hover:border-[#3d2817]/30'
                }`}
              >
                <LogIn className="mx-auto mb-1.5 h-5 w-5" />
                <div className="text-sm font-semibold">{t('guestReservation.payAsMember')}</div>
              </button>
            </div>
          </div>
        )}

        {currentUser && (
          <div className="border-b border-black/10 bg-[#2f9e44]/10 p-4">
            <div className="mb-1 flex items-center space-x-2 text-[#2f9e44]">
              <div className="h-2 w-2 animate-pulse rounded-full bg-[#2f9e44]" />
              <span className="text-sm font-semibold">
                {t('guestReservation.loggedInAs')}: {currentUser.firstName} {currentUser.lastName}
              </span>
            </div>
            <p className="text-xs text-[#5d4437]">{currentUser.email}</p>
          </div>
        )}

        {vumPromo && (
          <div className="border-b border-black/10 bg-emerald-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-emerald-800">
              <Ticket className="h-4 w-4" />
              <span className="text-sm font-semibold">Code promo VUM : {vumPromo.code}</span>
            </div>
            <p className="mb-1 text-xs font-medium text-emerald-700">{vumPromo.packageName}</p>
            <div className="flex items-center gap-4 text-xs text-emerald-800">
              <span className="flex items-center gap-1">
                <Moon className="h-3 w-3" />
                {vumPromo.nights} nuit{vumPromo.nights > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        )}

        {paymentMode === 'member' && !isLoggedIn && (
          <div className="border-b border-black/10 bg-white/60 p-4">
            <h3 className="mb-4 text-base font-semibold text-[#3d2817]">
              {t('guestReservation.memberLogin')}
            </h3>
            <form onSubmit={handleLogin} className="space-y-4" autoComplete="on">
              <div>
                <label htmlFor="grf-login-email" className="mb-2 block text-sm font-medium text-[#3d2817]">
                  {t('common.email')} *
                </label>
                <input
                  id="grf-login-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={loginData.email}
                  onChange={handleLoginInputChange}
                  required
                  className="w-full rounded-md border border-[#3d2817]/20 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2f9e44]/50"
                />
              </div>
              <div>
                <label
                  htmlFor="grf-login-password"
                  className="mb-2 block text-sm font-medium text-[#3d2817]"
                >
                  {t('guestReservation.password')} *
                </label>
                <input
                  id="grf-login-password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={loginData.password}
                  onChange={handleLoginInputChange}
                  required
                  className="w-full rounded-md border border-[#3d2817]/20 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2f9e44]/50"
                />
              </div>
              {loginError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                  {loginError}
                </div>
              )}
              <button
                type="submit"
                className="w-full rounded-lg bg-[#2f9e44] px-4 py-2 text-white transition-colors hover:bg-[#27863a]"
              >
                {t('common.login')}
              </button>
            </form>
          </div>
        )}

        {formReady && (
          <div key={itemId} className="space-y-4 p-4">
            <div className="rounded-xl border border-[#3d2817]/10 bg-white/80 p-3">
              <h3 className="mb-1 text-base font-semibold text-[#3d2817]">{itemName}</h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#5d4437]">
                  {itemType === 'accommodation' && t('common.perNightPerPerson')}
                  {itemType === 'activity' && t('common.perPerson')}
                  {itemType === 'package' && t('common.total')}
                </span>
                <span className="text-lg font-bold text-[#2f9e44]">${itemPrice}</span>
              </div>
            </div>

            <GuestReservationStepTracker
              step={wizardStep}
              step1Done={step1Done}
              step2Done={step2Done}
              step3Done={step3Done}
              labels={[
                t('guestReservation.dates') || 'Dates',
                t('guestReservation.personalInfo') || 'Info',
                t('guestReservation.paymentMethod') || 'Pay',
              ]}
              onSelectStep={selectStep}
            />

            <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
              {wizardStep === 1 ? (
                <>
                  <GuestReservationFormPersonalAndStayFields m={m} section="stay" />
                  <GuestReservationFormExtrasAndPayment m={m} section="note" />
                </>
              ) : null}

              {wizardStep === 2 ? (
                <GuestReservationFormPersonalAndStayFields m={m} section="identity" />
              ) : null}

              {wizardStep === 3 ? (
                <GuestReservationFormExtrasAndPayment m={m} section="pay" />
              ) : null}

              {paymentError && wizardStep < 3 ? (
                <p className="text-sm text-red-600">{paymentError}</p>
              ) : null}

              {wizardStep < 3 ? (
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={goBack}
                    className="flex-1 rounded-lg border border-[#3d2817]/20 px-4 py-3 text-[#3d2817] transition-colors hover:bg-white/70"
                  >
                    {wizardStep === 1 ? t('common.cancel') || 'Cancel' : t('common.goBack') || 'Back'}
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={wizardStep === 1 ? !step1Done : !step2Done}
                    className="flex-1 rounded-lg bg-[#2f9e44] px-4 py-3 font-semibold text-white transition-colors hover:bg-[#27863a] disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {t('common.next') || 'Next'}
                  </button>
                </div>
              ) : null}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
